import React from 'react';
import {
  ReportInput,
  ReportReason,
  REPORT_REASON_LABELS,
  reportContent,
} from '../../../services/social/api';
import './ReportModal.css';

/**
 * Report dialog (T2.12/REQ-78) — the in-app "flag this" surface Apple's UGC
 * rules require. Reused by every reportable thing (post, comment, profile,
 * trip) via `target`; the content-specific labels come from the opener.
 *
 * Deliberately says nothing about outcomes. The server never returns how many
 * others reported or whether the item was hidden — surfacing that would be a
 * brigading signal — so this only ever confirms "thanks, we'll take a look".
 */

export interface ReportTarget {
  targetType: ReportInput['targetType'];
  targetId: string;
  targetOwnerId?: string;
  /** e.g. "this post", "@ada", "this comment" — shown in the title. */
  label: string;
}

interface ReportModalProps {
  target: ReportTarget;
  onClose: () => void;
}

const ReportModal: React.FC<ReportModalProps> = ({ target, onClose }) => {
  const [reason, setReason] = React.useState<ReportReason | null>(null);
  const [note, setNote] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const submit = async () => {
    if (!reason || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await reportContent({
        targetType: target.targetType,
        targetId: target.targetId,
        targetOwnerId: target.targetOwnerId,
        reason,
        note: note.trim() || undefined,
      });
      setDone(true);
    } catch (err: any) {
      setError(err?.message || 'Could not submit the report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="v2-report-modal__scrim" role="presentation" onClick={onClose}>
      <div
        className="v2-report-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Report ${target.label}`}
        onClick={(event) => event.stopPropagation()}
      >
        {done ? (
          <div className="v2-report-modal__done">
            <h2>Thanks for letting us know</h2>
            <p>Our team will review {target.label}. We don’t share the outcome of reports.</p>
            <button type="button" className="v2-report-modal__primary" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <>
            <h2>Report {target.label}</h2>
            <p className="v2-report-modal__hint">Why are you reporting this?</p>

            <fieldset className="v2-report-modal__reasons">
              {REPORT_REASON_LABELS.map(({ value, label }) => (
                <label key={value} className="v2-report-modal__reason">
                  <input
                    type="radio"
                    name="report-reason"
                    value={value}
                    checked={reason === value}
                    onChange={() => setReason(value)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </fieldset>

            <textarea
              className="v2-report-modal__note"
              placeholder="Add anything that would help us review (optional)"
              maxLength={500}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />

            {error && <p className="v2-report-modal__error" role="alert">{error}</p>}

            <div className="v2-report-modal__actions">
              <button type="button" className="v2-report-modal__secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="v2-report-modal__primary"
                disabled={!reason || submitting}
                onClick={submit}
              >
                {submitting ? 'Submitting…' : 'Submit report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportModal;
